"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api/client";
export function useApi<T>(path: string | null, pollMs = 0) {
  const [state, setState] = useState<{
    data?: T;
    error?: Error;
    loading: boolean;
  }>({ loading: !!path });
  const [revision, setRevision] = useState(0);
  const reload = useCallback(() => setRevision((v) => v + 1), []);
  const latest = useRef(path);
  latest.current = path;
  const previousPath = useRef<string | null>(null);
  useEffect(() => {
    if (!path) {
      setState({ loading: false });
      return;
    }
    const controller = new AbortController();
    let busy = false;
    const changed = previousPath.current !== path;
    previousPath.current = path;
    setState((previous) =>
      changed
        ? { loading: true }
        : { data: previous.data, loading: previous.data === undefined },
    );
    async function load() {
      if (busy) return;
      busy = true;
      try {
        const data = await api<T>(path!, { signal: controller.signal });
        if (!controller.signal.aborted && latest.current === path)
          setState({ data, loading: false });
      } catch (error) {
        if (!controller.signal.aborted && latest.current === path)
          setState((previous) => ({
            data: previous.data,
            error: error as Error,
            loading: false,
          }));
      } finally {
        busy = false;
      }
    }
    void load();
    const timer = pollMs ? setInterval(load, pollMs) : undefined;
    return () => {
      controller.abort();
      if (timer) clearInterval(timer);
    };
  }, [path, revision, pollMs]);
  return { ...state, reload };
}
